import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectDecoratedDeliveryOptions } from 'src/redux/delivery/selectors';
import type { DecoratedDeliveryOption } from 'src/redux/delivery/types';
import { selectProductDeliveryByEvent } from 'src/redux/product/selectors';
import { getMaxDeliveryDate, hasExactDeliveryDate, isLateDelivery } from './utils';

const selectSelectedDecoratedDeliveryOptions = (state: ReduxApplicationState) =>
  selectDecoratedDeliveryOptions(state);

export const selectCheapestDeliveryOptionPrice = createSelector(
  [selectSelectedDecoratedDeliveryOptions, selectProductDeliveryByEvent],
  (deliveryOptions: DecoratedDeliveryOption[] | [], lastDeliveryByEvent): number | null => {
    if (deliveryOptions.length === 0 || !lastDeliveryByEvent?.arriveByEvent) {
      return null;
    }

    const cheapestDeliveryOptionPrice = (deliveryOptions as DecoratedDeliveryOption[]).reduce(
      (cheapestDeliveryPrice: number | null, option: DecoratedDeliveryOption) => {
        const { lastDeliveryBy } = lastDeliveryByEvent;
        const {
          date,
          max,
          price: { amount },
        } = option;

        const numberAmount = Number(amount);
        const hasExactDeliveryDateOption = hasExactDeliveryDate(option);

        const isTimelyExactDeliveryDateOption =
          hasExactDeliveryDateOption && !isLateDelivery(date!, lastDeliveryBy);

        if (hasExactDeliveryDateOption && !isTimelyExactDeliveryDateOption) {
          return cheapestDeliveryPrice;
        }

        const isCheaperDeliveryOption =
          cheapestDeliveryPrice == null || numberAmount < cheapestDeliveryPrice;

        if (isTimelyExactDeliveryDateOption && isCheaperDeliveryOption) {
          return numberAmount;
        }

        const maxDeliveryDate = getMaxDeliveryDate(max!);

        if (isLateDelivery(maxDeliveryDate, lastDeliveryBy)) {
          return cheapestDeliveryPrice;
        }

        return isCheaperDeliveryOption ? numberAmount : cheapestDeliveryPrice;
      },
      null as null | number,
    );

    return cheapestDeliveryOptionPrice;
  },
);

export const selectDeliveryByEventCost = createSelector(
  [selectCheapestDeliveryOptionPrice, selectProductDeliveryByEvent],
  (cost, deliveryByEvent) => {
    if (!deliveryByEvent?.arriveByEvent || cost == null) {
      return 'not set';
    }

    return cost || 'free';
  },
);
